import "bootstrap";
import { _ } from "../../polyglot/polyglot.js";
import { select, theme } from "../../elements/index.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import checkboxFieldWidget from "./fields/checkboxFieldWidget.js";
import colorPickerFieldWidget from "./fields/colorPickerFieldWidget.js";
import coordinatesFieldWidget from "./fields/coordinatesFieldWidget.js";
import datePickerWidget from "../import/datePickerWidget.js";
import FileUploadWidget from "../FileUploadWidget/FileUploadWidget.js";
import formWidget from "./formWidget.js";
import hiddenFieldWidget from "./fields/hiddenFieldWidget.js";
import inputFieldWidget from "./fields/inputFieldWidget.js";
import KeyFigureSelectionWidget from "../keyFigures/KeyFigureSelectionWidget/KeyFigureSelectionWidget.js";
import multipleCheckboxListFieldWidget from "./fields/multipleCheckboxListFieldWidget.js";
import passwordFieldWidget from "./fields/passwordFieldWidget.js";
import RadioFieldWidget from "./fields/RadioFieldWidget.js";
import regionWidget from "../regionWidget.js";
import renderableFieldWidget from "./fields/renderableFieldWidget.js";
import SwitchButton from "../SwitchButton/SwitchButton.js";
import textareaFieldWidget from "./fields/textareaFieldWidget.js";
import Timespan from "../../../models/period/Timespan.js";
import TreeWidget from "../tree/TreeWidget/TreeWidget.js";
import twoListMultipleSelectFieldWidget from "./fields/twoListMultipleSelectFieldWidget.js";
import wysiwygFieldWidget from "./fields/wysiwygFieldWidget/wysiwygFieldWidget.js";

/**
 * Provides methods to build a form by adding fields to it. To avoid
 * changing all users of formWidget, this class also renders the
 * underlying formWidget and provides the same API. As such, it
 * implements more than the Builder design pattern.
 */
export default class FormBuilder extends Widget {
	constructor(spec) {
		super(...arguments);

		this._form = formWidget(spec);
		this.fields = this._form.fields;
		this.onSubmit = this._form.onSubmit;
	}

	getObjectToSave(...rest) {
		return this._form.getObjectToSave(...rest);
	}

	invalidFields(...rest) {
		return this._form.invalidFields(...rest);
	}

	cancel(...rest) {
		return this._form.cancel(...rest);
	}

	submit(...rest) {
		return this._form.submit(...rest);
	}

	group(...rest) {
		return this._form.group(...rest);
	}

	legend(...rest) {
		return this._form.legend(...rest);
	}

	append(...rest) {
		return this._form.append(...rest);
	}

	addField(...rest) {
		return this._form.addField(...rest);
	}

	validate(...rest) {
		return this._form.validate(...rest);
	}

	isEmpty(...rest) {
		return this._form.isEmpty(...rest);
	}

	isValid(...rest) {
		return this._form.isValid(...rest);
	}

	hidden(spec) {
		return this.addField(hiddenFieldWidget(spec), spec);
	}

	input(spec) {
		return this.addField(inputFieldWidget(spec), spec);
	}

	textarea(spec) {
		return this.addField(textareaFieldWidget(spec), spec);
	}

	wysiwyg(spec) {
		return this.addField(wysiwygFieldWidget(spec), spec);
	}

	password(spec) {
		return this.addField(
			inputFieldWidget(
				Object.assign(spec, {
					type: "password",
				}),
			),
			spec,
		);
	}

	passwordWithValidation(spec) {
		return this.addField(passwordFieldWidget(spec), spec);
	}

	checkbox(spec) {
		return this.addField(checkboxFieldWidget(spec), spec);
	}

	coordsInput(spec) {
		return this.addField(coordinatesFieldWidget(spec), spec);
	}

	twoListMultipleSelectField(spec) {
		return this.addField(twoListMultipleSelectFieldWidget(spec), spec);
	}

	multipleCheckboxListField(spec) {
		return this.addField(multipleCheckboxListFieldWidget(spec), spec);
	}

	colorPicker(spec) {
		return this.addField(colorPickerFieldWidget(spec), spec);
	}

	/**
	 * Allow to add any renderable to a form.
	 */
	addRenderable(spec) {
		return this.addField(renderableFieldWidget(spec), spec);
	}

	switchButton(spec) {
		const buttonSpec = { ...spec, dataTestName: spec.name };
		const switchButtonWidget = new SwitchButton(buttonSpec);
		switchButtonWidget.stateChanged.register(spec.onStateChange);

		const fieldSpec = {
			...spec,
			content: switchButtonWidget,
			getFormValue: () => switchButtonWidget.getState(),
			setFormValue: (newValue) => switchButtonWidget.setState(newValue),
		};

		this.addRenderable(fieldSpec);

		return switchButtonWidget;
	}

	fileUploadField(spec) {
		const buttonSpec = {
			...spec,
			label: _("Upload"),
			buttonCssClass: "btn btn-default",
			cssClass: spec.cssClass,
			dataTestName: spec.name,
		};

		const button = new FileUploadWidget(buttonSpec);
		const fieldSpec = {
			...spec,
			content: button,
			getFormValue: () => button.getAttachment(),
			setFormValue: (...rest) => button.setAttachment(...rest),
		};

		this.addRenderable(fieldSpec);

		return button;
	}

	keyFigurePickerField(spec) {
		const picker = new KeyFigureSelectionWidget(spec);

		const defaultSpec = {
			label: _("Key figure"),
			content: picker,
			getFormValue: () => picker.getSelection(),
			setFormValue: (keyFigure) => picker.selectKeyFigure(keyFigure),
		};

		this.addRenderable({ ...defaultSpec, ...spec });

		return picker;
	}

	imageUploadField(spec) {
		const buttonSpec = { ...spec, wantsPreview: true };
		return this.fileUploadField(buttonSpec);
	}

	newDropDownField(spec) {
		return this.selectField({
			allowEmptySelection: spec.noDefaultSelection,
			emptyLabel: spec.noSelectionText,
			dataTest: spec.dataTestName,
			searchable: spec.liveSearch,
			...spec,
		});
	}

	selectField(spec) {
		const validator = (data) => {
			if (spec.noValidation || !spec.validator) {
				return;
			}

			if (!data) {
				throw new Error(_("You must select an item"));
			}

			if (spec.validator) {
				spec.validator(data);
			}
		};

		spec.searchable ??= spec.items.length > 5;

		const dropdownAttributes = spec.attributes || {};
		spec.name && (dropdownAttributes.name = spec.name);

		let state = spec.selected;
		const region = regionWidget({
			content: (html) =>
				theme(
					{
						class: spec.themeClass,
						name: spec.themeName,
						renderOn: html,
					},
					select({
						dataTest: spec.dataTest,
						disabled: spec.disabled,
						class: ["form-control", spec.class],
						selected: state,
						onChange: (value) => {
							state = value;
							spec.onChange?.(state);
						},
						items: spec.items,
						ref: (ref) => {
							Object.keys(dropdownAttributes).forEach((key) => {
								ref.setAttribute(key, dropdownAttributes[key]);
							});
						},
						allowEmptySelection: spec.allowEmptySelection,
						emptyLabel:
							spec.emptyLabel || _("— select an option —"),
						searchable: spec.searchable,
					}),
				),
		});

		const fieldSpec = {
			...spec,
			content: region,
			getFormValue: () => state,
			setFormValue: (newValue) => {
				state = newValue;
				region.update();
			},
			validator,
		};

		this.addRenderable(fieldSpec);

		return region;
	}

	datePickerField(spec = {}) {
		const datePicker = datePickerWidget({
			date: spec.date || new Date(),
			cssClass: ["form-control", spec.klass],
		});

		datePicker.setRange(
			new Timespan({
				start: spec.startDate || new Date(0),
				end: spec.endDate || new Date(2984, 11, 31),
			}),
		);

		this.addRenderable({
			content: datePicker,
			getFormValue: () => datePicker.getSelectedDate(),
			setFormValue: (date) => datePicker.setSelectedDate(date),
			validator: spec.validator || (() => {}),
			attribute: spec.attribute || "datePicker",
			label: spec.label,
		});

		return datePicker;
	}

	tree(spec) {
		const tree = new TreeWidget(spec);

		const fieldSpec = {
			...spec,
			content: tree,
			getFormValue: () => tree.selectedNode(),
			setFormValue: (newValue) => newValue && tree.selectNode(newValue),
		};

		this.addRenderable(fieldSpec);

		return tree;
	}

	radio(spec) {
		const radio = new RadioFieldWidget(spec);

		const fieldSpec = {
			...spec,
			content: radio,
			getFormValue: () => radio.isChecked(),
			setFormValue: (newValue) => newValue && radio.check(newValue),
		};

		this.addRenderable(fieldSpec);

		return radio;
	}

	renderOn(html) {
		html.render(this._form);
	}
}
